import React from 'react'
import { useCallback, useMemo } from 'react'
import { Highlight, useInfiniteHits } from 'react-instantsearch'
import DOMPurify from 'isomorphic-dompurify'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'

import LinkComponent from '@/components/link-component'
import SafeHTML from '@/components/ui/safe-html'
import { useTranslations } from '@/utils/locale'
import { cx } from '@/utils/strings'
import Routes from '@/constants/routes'

import { HitProps } from './types'

type HitPreviewProps = {
  hit: HitProps
  onLinkClick?: (hit: HitProps, e: React.MouseEvent<HTMLAnchorElement>) => void
}

export default function HitPreview({ hit, onLinkClick }: HitPreviewProps) {
  const { t } = useTranslations()
  const { sendEvent } = useInfiniteHits<HitProps>()

  const highlightedBody = useMemo(() => {
    if (!hit?._highlightResult?.body) return ''
    const parser = new DOMParser()
    let body: string = (hit?._highlightResult?.body as any).value || ''
    if (hit.type === 'pages') {
      body = body.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
        const cleanUrl = url.replace(/<[^>]+>/g, '')
        return `[${text}](${cleanUrl})`
      })
    }
    // Escape <mark> and </mark> tags
    body = body
      .replace(/<mark>/g, '&lt;mark class="ais-Highlight-highlighted dark:text-secondary-900"&gt;')
      .replace(/<\/mark>/g, '&lt;/mark&gt;')
    const decoded = parser.parseFromString(body, 'text/html').documentElement.textContent
    return DOMPurify.sanitize(decoded)
  }, [hit])

  const isSection = !!hit.hash
  // Remove the last breadcrumb if it's the same as the title
  const breadcrumbs = hit.breadcrumbs?.slice(0, -1) || []

  const Link = useCallback(
    ({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) => (
      <LinkComponent
        to={to}
        className={className}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          onLinkClick?.(hit, e)
          sendEvent('click', hit, 'Result Clicked')
          sendEvent('conversion', hit, 'Result Converted')
        }}
      >
        {children}
      </LinkComponent>
    ),
    [hit, onLinkClick]
  )

  return (
    <div className="hidden md:block w-1/2 pt-10 px-6 pb-6 overflow-auto bg-secondary-50 dark:bg-secondary-800">
      <Link to={`${hit.url}${hit.hash ? `#${hit.hash}` : ''}`} className="hover:underline">
        <div className={cx('text-xs text-secondary-500 dark:text-secondary-400 mb-4')}>
          {!hit.url.toLowerCase().startsWith('http') && Routes.hostname}
          {hit.url}
          {hit.hash && `#${hit.hash}`}
        </div>
      </Link>
      <div className="flex mb-4">
        {breadcrumbs.map((breadcrumb, index) => (
          <div key={index} className="inline-flex items-center justify-center whitespace-pre">
            {index > 0 && <span className="bwi bwi-angle-right text-secondary-400 dark:text-secondary-300 mx-2" />}
            {breadcrumb}
          </div>
        ))}
      </div>

      {!isSection && (
        <Link
          to={hit.url}
          className="hover:text-primary-700 dark:hover:text-secondary-300 transition-colors duration-200"
        >
          <h3>
            <Highlight
              hit={hit}
              attribute="title"
              classNames={{
                highlighted: cx('dark:text-secondary-900'),
              }}
            />
          </h3>
        </Link>
      )}
      {isSection && (
        <>
          <Link
            to={hit.url}
            className="hover:text-primary-700 dark:hover:text-secondary-300 transition-colors duration-200"
          >
            <h3 className="mb-8">{hit.breadcrumbs?.[hit.breadcrumbs.length - 1]}</h3>
          </Link>
          <Link
            to={`${hit.url}#${hit.hash}`}
            className="hover:text-primary-700 dark:hover:text-secondary-300 transition-colors duration-200"
          >
            <h4 className="h6 flex items-center gap-1">
              <span className="bwi bwi-hashtag text-2xl" />
              <Highlight
                hit={hit}
                attribute="title"
                classNames={{
                  highlighted: cx('dark:text-secondary-900'),
                }}
              />
            </h4>
          </Link>
        </>
      )}
      {highlightedBody && hit.type !== 'pages' && (
        <SafeHTML className="text-base mb-12 [&_ul]:pl-6 [&_ol]:pl-6" html={highlightedBody} />
      )}
      {highlightedBody && hit.type === 'pages' && (
        <div className="text-base mb-12 [&_ul]:pl-6 [&_ol]:pl-6">
          <Markdown
            disallowedElements={['img']}
            components={{
              h1: ({ children }) => <p className="text-2xl font-bold mt-2">{children}</p>,
              h2: ({ children }) => <p className="text-xl font-bold mt-2">{children}</p>,
              h3: ({ children }) => <p className="text-lg font-bold mt-2">{children}</p>,
              h4: ({ children }) => <p className="text-base font-bold mt-2">{children}</p>,
              h5: ({ children }) => <p className="text-sm font-bold mt-2">{children}</p>,
              h6: ({ children }) => <p className="text-sm font-bold mt-2">{children}</p>,
              hr: () => <hr className="my-4" />,
              th: ({ children }) => <th className="p-2 text-left">{children}</th>,
              td: ({ children }) => <td className="p-2">{children}</td>,
              tr: ({ children }) => <tr className="not-last:border-b">{children}</tr>,
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noreferrer noopener">
                  {children}
                </a>
              ),
              mark: ({ children }) => (
                <mark className="ais-Highlight-highlighted dark:text-secondary-900 p-0!">{children}</mark>
              ),
            }}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[
              rehypeRaw,
              [
                rehypeSanitize,
                // Allow <br> and <sub> but still strip scripts/event handlers
                {
                  ...defaultSchema,
                  tagNames: [...(defaultSchema.tagNames ?? []), 'mark', 'sub', 'sup'],
                  protocols: {
                    ...defaultSchema.protocols,
                    href: ['http', 'https', 'mailto'],
                  },
                },
              ],
            ]}
          >
            {highlightedBody}
          </Markdown>
        </div>
      )}
      {(hit.toc?.length ?? 0) > 0 && (
        <>
          <p className="text-base text-secondary-900 dark:text-white uppercase mb-2">{t('On this page')}</p>
          {hit.toc?.map((toc, index) => {
            return (
              <Link
                key={index}
                className={cx(
                  'flex items-center text-base text-neutral-600 dark:text-white leading-8 hover:text-primary-700 dark:hover:text-secondary-300 transition-colors duration-200',
                  toc.level === 3 && 'pl-4',
                  toc.hash === `#${hit.hash}` &&
                    'text-primaryBlue! hover:text-primary-700! dark:hover:text-secondary-300!'
                )}
                to={`${hit.url}${toc.hash}`}
              >
                {/* <ArrowCircleRightIcon className="text-primaryBlue w-4 h-4 mr-2" /> */}
                <span className="bwi bwi-arrow-right text-primaryBlue w-4 h-4 mr-2" />
                {toc.title}
              </Link>
            )
          })}
        </>
      )}
    </div>
  )
}
